import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '../lib/twitch.js';
import { apiFetch } from '../lib/api.js';

/**
 * Fetches and caches Twitch channel profile images for all unique channels
 * present in the message list. Used to show per-channel avatars in shared
 * chat (StreamTogether).
 */
export function useChannelAvatars(messages: ChatMessage[]): Map<string, string> {
  const [avatars, setAvatars] = useState<Map<string, string>>(() => new Map());
  const fetchedRef = useRef<Set<string>>(new Set());
  const pendingRef = useRef<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const newLogins = new Set<string>();
    for (const m of messages) {
      if (m.source !== 'twitch') continue;
      const login = m.channel?.toLowerCase();
      if (login && !fetchedRef.current.has(login) && !pendingRef.current.has(login)) {
        newLogins.add(login);
      }
    }
    if (newLogins.size === 0) return;

    for (const l of newLogins) pendingRef.current.add(l);

    // Debounce so multiple new channels in one render are batched
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const batch = [...pendingRef.current];
      if (batch.length === 0) return;
      pendingRef.current = new Set();

      apiFetch<{ avatars: Record<string, string> }>(
        `/api/channels/avatars?logins=${batch.join(',')}`,
      )
        .then((data) => {
          for (const login of batch) fetchedRef.current.add(login);
          if (Object.keys(data.avatars).length === 0) return;
          setAvatars((prev) => {
            const next = new Map(prev);
            for (const [login, url] of Object.entries(data.avatars)) {
              next.set(login.toLowerCase(), url);
            }
            return next;
          });
        })
        .catch(() => {
          // On error mark as fetched so we don't retry endlessly
          for (const login of batch) fetchedRef.current.add(login);
        });
    }, 200);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [messages]);

  return avatars;
}
